import { Stack } from '@startuptools/ui/Stack';
import { GqlMeeting, GqlMeetingKind, GqlSnrAuthorization } from '../../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { CreateMeetingDialog } from './meeting/CreateMeetingDialog';
import { Suspense, useState } from 'react';
import { FormValues } from './meeting/MeetingDetailsForm';
import { SubmitHandler } from 'react-hook-form';
import { MeetingSelectDialog } from './MeetingSelectDialog';
import { Meeting } from './Meeting';
import { Card } from '@startuptools/ui/Card';
import { Dropdown } from '@startuptools/ui/Dropdown';
import { MenuButton } from '@startuptools/ui/MenuButton';
import { Menu } from '@startuptools/ui/Menu';
import { MenuItem } from '@startuptools/ui/MenuItem';
import { Alert } from '@startuptools/ui/Alert';
import { AuthorizationSelectDialog } from '../../../components/select-authorization-dialog/AuthorizationSelectDialog';
import { Box } from '@startuptools/ui/Box';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { DateTime } from 'luxon';
import { MeetingMinutesDocument } from './documents/MeetingMinutes';
import { DisplayDocument } from '@startuptools/business-ui/document/Document';
import { Divider } from '@startuptools/ui/Divider';
import { DateSettings } from './meeting/create-meeting.utils';

export type HandleCreateMeetingFn = SubmitHandler<FormValues & { kind: GqlMeetingKind; usesStepper: boolean }>;

export const StepMeeting = ({
  meeting,
  snrAuth,
  allowMeetingKinds,
  hasActiveSubscription,
  handleCreateMeeting,
  handleConnectMeeting,
  handleDisconnectMeeting,
  handleSelectAuth,
  handleDisconnectAuth,
  showTitle = true,
  dateSettings,
  loadingStates,
}: {
  meeting:
    | (Pick<GqlMeeting, 'id' | 'kind' | 'number' | 'heldKind' | 'startTime' | 'usesStepper'> & {
        latestMinutesDocument?: DisplayDocument | null;
      })
    | null
    | undefined;
  snrAuth:
    | Pick<GqlSnrAuthorization, 'id' | 'registrationDate' | 'filingNumber' | 'filingYear' | 'auths'>
    | null
    | undefined;
  allowMeetingKinds: GqlMeetingKind[];
  hasActiveSubscription: boolean;
  handleCreateMeeting: HandleCreateMeetingFn;
  handleConnectMeeting: (meetingId: string) => Promise<unknown>;
  handleDisconnectMeeting: () => Promise<unknown>;
  handleSelectAuth: ((snrAuthId: string) => Promise<unknown>) | null;
  handleDisconnectAuth: (() => Promise<unknown>) | null;
  showTitle?: boolean;
  dateSettings?: DateSettings;
  loadingStates: {
    create: boolean;
    connect: boolean;
    disconnect: boolean;
  };
}) => {
  const { i18n } = useLingui();
  const [showCreateDialog, setShowCreateDialog] = useState<{ kind: GqlMeetingKind } | null>(null);
  const [showSelectDialog, setShowSelectDialog] = useState<{ kind: GqlMeetingKind } | null>(null);
  const [showSelectAuthDialog, setShowSelectAuthDialog] = useState(false);
  const handleCreate: SubmitHandler<FormValues> = values => {
    handleCreateMeeting({ ...values, kind: showCreateDialog!.kind, usesStepper: true });
  };

  const getMeetingMenuItem = (kind: GqlMeetingKind, key: number) => {
    switch (kind) {
      case GqlMeetingKind.ExtraShareholdersMeeting:
        return (
          <MenuItem key={key} onClick={() => setShowCreateDialog({ kind: GqlMeetingKind.ExtraShareholdersMeeting })}>
            <Trans id="Extra General Meeting" />
          </MenuItem>
        );
      case GqlMeetingKind.ShareholdersMeeting:
        return (
          <MenuItem key={key} onClick={() => setShowCreateDialog({ kind: GqlMeetingKind.ShareholdersMeeting })}>
            <Trans id="Annual General Meeting" />
          </MenuItem>
        );
      case GqlMeetingKind.BoardMeeting:
        return (
          <MenuItem key={key} onClick={() => setShowCreateDialog({ kind: GqlMeetingKind.BoardMeeting })}>
            <Trans id="Board Meeting" />
          </MenuItem>
        );
    }
  };

  return (
    <>
      <Stack gap={3}>
        {showTitle && (
          <Typography level="h4">
            <Trans id="Meeting" />
          </Typography>
        )}

        {meeting ? (
          <>
            <Card>
              <Stack direction="row" gap={2} alignItems="center">
                <Stack gap={1}>
                  <Meeting meeting={meeting} />
                </Stack>
                <Button variant="outlined" onClick={handleDisconnectMeeting} loading={loadingStates?.disconnect}>
                  <Trans id="Disconnect" />
                </Button>
              </Stack>
              <Divider />
              <MeetingMinutesDocument meeting={meeting} hasActiveSubscription={hasActiveSubscription} />
            </Card>
            {meeting.kind === GqlMeetingKind.BoardMeeting && handleSelectAuth && handleDisconnectAuth && (
              <Card>
                <Alert>
                  <Trans id="Select an authorization unless you want to later approve on a general meeting" />
                </Alert>

                {snrAuth ? (
                  <Stack direction="row" gap={2} alignItems="center">
                    <Stack gap={1}>
                      <Typography level="title-md">
                        <Trans
                          id="Registered: <dateFormat>{date}</dateFormat>"
                          components={{ dateFormat: <DateFormat locale={i18n.locale} /> }}
                          values={{ date: snrAuth.registrationDate }}
                        />
                      </Typography>
                      <Typography level="body-sm">
                        <Trans
                          id="Valid Thru: <dateFormat>{date}</dateFormat>"
                          components={{
                            dateFormat: (
                              <DateFormat
                                locale={i18n.locale}
                                format={snrAuth.auths[0]!.before_year ? { year: 'numeric' } : DateTime.DATE_SHORT}
                              />
                            ),
                          }}
                          values={{ date: snrAuth.auths[0]!.before_year || snrAuth.auths[0]!.before }}
                        />
                      </Typography>
                    </Stack>
                    <Box>
                      <Button variant="outlined" onClick={handleDisconnectAuth} loading={loadingStates?.disconnect}>
                        <Trans id="Disconnect" />
                      </Button>
                    </Box>
                  </Stack>
                ) : (
                  <Box>
                    <Button onClick={() => setShowSelectAuthDialog(true)}>
                      <Trans id="Select Authorization" />
                    </Button>
                  </Box>
                )}
              </Card>
            )}
          </>
        ) : (
          <Card>
            <Stack direction="row" gap={1}>
              {allowMeetingKinds.length === 1 ? (
                <Button
                  onClick={() => setShowCreateDialog({ kind: allowMeetingKinds[0]! })}
                  loading={loadingStates?.create}
                >
                  <Trans id="New Meeting" />
                </Button>
              ) : (
                <Dropdown>
                  <MenuButton color="primary" variant="solid" loading={loadingStates?.create}>
                    <Trans id="New Meeting" />
                  </MenuButton>
                  <Menu>{allowMeetingKinds.map((k, i) => getMeetingMenuItem(k, i))}</Menu>
                </Dropdown>
              )}
              <Button
                variant="outlined"
                onClick={() => setShowSelectDialog({ kind: GqlMeetingKind.BoardMeeting })}
                loading={loadingStates?.connect}
              >
                <Trans id="Existing Meeting" />
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>
      {showCreateDialog && (
        <Suspense>
          <CreateMeetingDialog
            usesStepper
            dateSettings={dateSettings}
            meetingKind={showCreateDialog.kind}
            onClose={() => setShowCreateDialog(null)}
            onSubmit={handleCreate}
          />
        </Suspense>
      )}
      {showSelectDialog && (
        <Suspense>
          <MeetingSelectDialog
            onClose={() => setShowSelectDialog(null)}
            onSelected={handleConnectMeeting}
            allowMeetingKinds={allowMeetingKinds}
          />
        </Suspense>
      )}
      {showSelectAuthDialog && handleSelectAuth && (
        <Suspense>
          <AuthorizationSelectDialog onClose={() => setShowSelectAuthDialog(false)} onSelected={handleSelectAuth} />
        </Suspense>
      )}
    </>
  );
};
